/**
 * /predict — Lenavio Predict.
 *
 * HR_ADMIN+, plan-gated by ``predictive_analytics``.
 *
 * Two-phase loading (introduced 2026-05): each tab loads the LATEST
 * CACHED analysis from the database on visit. No tokens are spent
 * just by clicking the page. A "Run new analysis" button at the top
 * of each tab triggers a fresh Claude call (with confirmation), which
 * overwrites the cache row and re-renders.
 *
 * Four sections behind a tabset:
 *   - Insights — AI-generated weekly leadership briefing.
 *   - Flight risk — every active employee scored 0-100, sortable +
 *     color-coded; click to see factors and recommended actions.
 *   - Forecast — bar chart of predicted 90-day turnover by department.
 *   - Engagement — scatter of avg engagement vs 12-month turnover, with
 *     flagged departments highlighted.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Brain,
  CheckCircle2,
  Loader2,
  Sparkles,
  TrendingDown,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";

import {
  fetchEngagementTrends,
  fetchFlightRisk,
  fetchTurnoverForecast,
  fetchWeeklyInsights,
  runEngagementTrends,
  runFlightRisk,
  runTurnoverForecast,
  runWeeklyInsights,
  type EngagementCorrelation,
  type FlightRiskReport,
  type FlightRiskRow,
  type RiskLevel,
  type TurnoverForecast,
  type WeeklyInsights,
} from "@/api/predict";

import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Tabs, TabPanel } from "@/components/ui/Tabs";
import { RunAnalysisHeader } from "@/components/predict/RunAnalysisHeader";

type TabId = "insights" | "risk" | "forecast" | "engagement";

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function riskTone(level: RiskLevel) {
  return level === "high"
    ? "danger"
    : level === "medium"
      ? "warning"
      : "success";
}

/** A payload counts as "empty" (first-time, never run) when the cache
 *  metadata is absent AND the underlying data arrays are empty.
 *  Differentiates "haven't run yet" from "ran and got nothing." */
function isFirstTime(payload: { cache_generated_at?: string | null }) {
  return !payload?.cache_generated_at;
}

// ════════════════════════════════════════════════════════════════════
// Insights tab
// ════════════════════════════════════════════════════════════════════

function InsightsTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<WeeklyInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchWeeklyInsights();
        if (!cancelled) setData(r);
      } catch {
        if (!cancelled) addToast("error", "Failed to load insights");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  async function runFresh() {
    setRunning(true);
    try {
      const r = await runWeeklyInsights();
      setData(r);
      addToast("success", "Weekly insights refreshed");
    } catch {
      addToast("error", "Failed to run insights");
    } finally {
      setRunning(false);
    }
  }

  if (loading) {
    return (
      <Card className="flex h-32 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </Card>
    );
  }

  const empty = !data || isFirstTime(data);

  return (
    <div className="space-y-4">
      <RunAnalysisHeader
        generatedAt={data?.cache_generated_at || data?.generated_at}
        isRunning={running}
        onRun={runFresh}
        emptyState={empty}
        confirmMessage="This will generate a fresh weekly briefing using Claude. Continue?"
      />

      {empty ? null : (
        <>
          <Card className="p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Sparkles className="h-4 w-4" /> Weekly briefing
            </div>
            <p className="text-sm">{data!.summary}</p>
          </Card>

          {data!.items.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {data!.items.map((it, i) => (
                <Card key={i} className="p-5">
                  <div className="mb-2 flex items-center gap-2">
                    {it.severity === "warning" ? (
                      <AlertTriangle className="h-4 w-4 text-warning" />
                    ) : it.severity === "success" ? (
                      <CheckCircle2 className="h-4 w-4 text-success" />
                    ) : (
                      <Sparkles className="h-4 w-4 text-accent" />
                    )}
                    <h3 className="text-sm font-semibold">{it.title}</h3>
                  </div>
                  <p className="text-sm text-text-muted">{it.body}</p>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Flight risk tab
// ════════════════════════════════════════════════════════════════════

function FlightRiskTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<FlightRiskReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState<RiskLevel | "all">("all");
  const [open, setOpen] = useState<FlightRiskRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchFlightRisk();
        if (!cancelled) setData(r);
      } catch {
        if (!cancelled) addToast("error", "Failed to load flight risk");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  async function runFresh() {
    setRunning(true);
    try {
      const r = await runFlightRisk();
      setData(r);
      addToast("success", "Flight-risk analysis refreshed");
    } catch {
      addToast("error", "Failed to run flight risk");
    } finally {
      setRunning(false);
    }
  }

  const visible = useMemo(() => {
    if (!data) return [];
    return filter === "all"
      ? data.rows
      : data.rows.filter((r) => r.risk_level === filter);
  }, [data, filter]);

  if (loading) {
    return (
      <Card className="flex h-32 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </Card>
    );
  }

  const empty = !data || isFirstTime(data);

  return (
    <div className="space-y-4">
      <RunAnalysisHeader
        generatedAt={data?.cache_generated_at || data?.generated_at}
        isRunning={running}
        onRun={runFresh}
        emptyState={empty}
        confirmMessage="This will rate every active employee on flight risk via Claude. Continue?"
      />

      {empty ? null : (
        <>
          <Card className="p-5">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Stat label="Analyzed" value={data!.total_analyzed} />
              <Stat label="High" value={data!.high_count} tone="danger" />
              <Stat label="Medium" value={data!.medium_count} tone="warning" />
              <Stat label="Low" value={data!.low_count} tone="success" />
            </div>
            {data!.notes && (
              <p className="mt-3 text-xs text-text-muted">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                {data!.notes}
              </p>
            )}
          </Card>

          <div className="flex gap-2">
            {(["all", "high", "medium", "low"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`rounded-full border px-3 py-1 text-xs capitalize ${
                  filter === k
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-border text-text-muted hover:text-text-primary"
                }`}
              >
                {k}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((r) => (
              <Card
                key={r.employee_id}
                className="cursor-pointer p-4 hover:bg-surface-muted/40"
                onClick={() => setOpen(r)}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold">
                      {r.first_name} {r.last_name}
                    </h3>
                    <div className="text-xs text-text-muted">
                      {r.title}
                      {r.department && <> · {r.department}</>}
                    </div>
                  </div>
                  <Badge variant={riskTone(r.risk_level)}>{r.risk_level}</Badge>
                </div>
                <div className="text-2xl font-semibold tabular-nums">
                  {r.risk_score}
                  <span className="text-sm text-text-muted">/100</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-surface-muted">
                  <div
                    className={`h-2 rounded-full ${
                      r.risk_level === "high"
                        ? "bg-danger"
                        : r.risk_level === "medium"
                          ? "bg-warning"
                          : "bg-success"
                    }`}
                    style={{ width: `${r.risk_score}%` }}
                  />
                </div>
              </Card>
            ))}
          </div>

          {open && <RiskDetail row={open} onClose={() => setOpen(null)} />}
        </>
      )}
    </div>
  );
}

function RiskDetail({
  row,
  onClose,
}: {
  row: FlightRiskRow;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <Card
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">
              {row.first_name} {row.last_name}
            </h3>
            <div className="text-sm text-text-muted">
              {row.title}
              {row.department && <> · {row.department}</>}
            </div>
          </div>
          <Badge variant={riskTone(row.risk_level)}>
            {row.risk_level} · {row.risk_score}
          </Badge>
        </div>

        <div className="mb-4">
          <h4 className="mb-2 text-sm font-medium text-warning">Risk factors</h4>
          {row.risk_factors.length === 0 ? (
            <p className="text-sm text-text-muted">None identified.</p>
          ) : (
            <ul className="list-disc pl-5 text-sm">
              {row.risk_factors.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="mb-4">
          <h4 className="mb-2 text-sm font-medium text-success">
            Recommended actions
          </h4>
          {row.recommended_actions.length === 0 ? (
            <p className="text-sm text-text-muted">No actions suggested.</p>
          ) : (
            <ul className="list-disc pl-5 text-sm">
              {row.recommended_actions.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1 text-sm hover:bg-surface-muted"
          >
            Close
          </button>
        </div>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "danger" | "warning" | "success";
}) {
  const cls =
    tone === "danger"
      ? "text-danger"
      : tone === "warning"
        ? "text-warning"
        : tone === "success"
          ? "text-success"
          : "";
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${cls}`}>
        {value}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Forecast tab
// ════════════════════════════════════════════════════════════════════

function ForecastTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<TurnoverForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchTurnoverForecast();
        if (!cancelled) setData(r);
      } catch {
        if (!cancelled) addToast("error", "Failed to load forecast");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  async function runFresh() {
    setRunning(true);
    try {
      const r = await runTurnoverForecast();
      setData(r);
      addToast("success", "Turnover forecast refreshed");
    } catch {
      addToast("error", "Failed to run forecast");
    } finally {
      setRunning(false);
    }
  }

  const max = useMemo(() => {
    if (!data) return 1;
    return Math.max(0.05, ...data.departments.map((d) => d.forecast_90d_pct));
  }, [data]);

  if (loading) {
    return (
      <Card className="flex h-32 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </Card>
    );
  }

  const empty = !data || isFirstTime(data);

  return (
    <div className="space-y-4">
      <RunAnalysisHeader
        generatedAt={data?.cache_generated_at || data?.generated_at}
        isRunning={running}
        onRun={runFresh}
        emptyState={empty}
        confirmMessage="This will recompute department-level turnover forecasts. Continue?"
      />

      {empty ? null : (
        <>
          <Card className="p-5">
            <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
              <Stat
                label="Org 12-mo turnover"
                value={fmtPct(data!.org_avg_turnover_12m)}
              />
              <Stat
                label="Org 90-day forecast"
                value={fmtPct(data!.org_forecast_90d_pct)}
              />
              <Stat label="Horizon" value={`${data!.horizon_days} days`} />
            </div>
          </Card>

          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium">
              <TrendingDown className="h-4 w-4" /> Predicted turnover by department
            </div>
            {data!.departments.length === 0 ? (
              <p className="text-sm text-text-muted">No data.</p>
            ) : (
              <div className="space-y-3">
                {data!.departments.map((d) => {
                  const w = (d.forecast_90d_pct / max) * 100;
                  return (
                    <div key={d.department_id || d.department_name || "none"}>
                      <div className="flex items-center justify-between text-sm">
                        <span>
                          {d.department_name || "(no department)"}{" "}
                          <span className="text-text-muted">
                            ({d.employee_count})
                          </span>
                        </span>
                        <span className="tabular-nums">
                          {fmtPct(d.forecast_90d_pct)}{" "}
                          <span className="text-text-muted">
                            · ~{d.expected_departures} expected ·{" "}
                            {d.confidence}
                          </span>
                        </span>
                      </div>
                      <div className="mt-1 h-2 w-full rounded-full bg-surface-muted">
                        <div
                          className={`h-2 rounded-full ${
                            d.forecast_90d_pct >= 0.05
                              ? "bg-warning"
                              : "bg-accent"
                          }`}
                          style={{ width: `${Math.max(2, w)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Engagement scatter tab
// ════════════════════════════════════════════════════════════════════

function EngagementTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<EngagementCorrelation | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchEngagementTrends();
        if (!cancelled) setData(r);
      } catch {
        if (!cancelled) addToast("error", "Failed to load engagement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  async function runFresh() {
    setRunning(true);
    try {
      const r = await runEngagementTrends();
      setData(r);
      addToast("success", "Engagement trends refreshed");
    } catch {
      addToast("error", "Failed to run engagement");
    } finally {
      setRunning(false);
    }
  }

  if (loading) {
    return (
      <Card className="flex h-32 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </Card>
    );
  }

  const empty = !data || isFirstTime(data);

  // Scatter: x = engagement (1-5), y = turnover rate (0..maxTurnover).
  const scored = (data?.departments ?? []).filter(
    (d) => d.avg_engagement_score != null,
  );
  const maxTurnover = Math.max(
    0.05,
    ...scored.map((d) => d.turnover_rate_12m),
  );

  return (
    <div className="space-y-4">
      <RunAnalysisHeader
        generatedAt={data?.cache_generated_at || data?.generated_at}
        isRunning={running}
        onRun={runFresh}
        emptyState={empty}
        confirmMessage="This will recompute engagement vs turnover correlations. Continue?"
      />

      {empty ? null : (
        <>
          <Card className="p-5">
            <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
              <Stat
                label="Org engagement"
                value={
                  data!.org_avg_engagement != null
                    ? data!.org_avg_engagement.toFixed(2)
                    : "—"
                }
                tone={undefined}
              />
              <Stat
                label="Org performance"
                value={
                  data!.org_avg_performance != null
                    ? data!.org_avg_performance.toFixed(2)
                    : "—"
                }
              />
              <Stat
                label="Departments flagged"
                value={data!.departments.filter((d) => d.flagged).length}
                tone="warning"
              />
            </div>
            <p className="mt-3 text-xs text-text-muted">
              Departments are flagged when avg engagement is &gt;0.5 below the
              org average AND 12-month turnover is at least 10%.
            </p>
          </Card>

          <Card className="p-5">
            <div className="mb-3 text-sm font-medium">
              Engagement vs 12-month turnover
            </div>
            {scored.length === 0 ? (
              <p className="text-sm text-text-muted">
                Need at least one pulse survey response per department.
              </p>
            ) : (
              <div className="relative h-64 w-full rounded-md border border-border bg-surface-muted">
                {/* Axis labels */}
                <div className="absolute bottom-1 left-2 text-xs text-text-muted">
                  ← lower engagement
                </div>
                <div className="absolute right-2 top-1 text-xs text-text-muted">
                  higher turnover ↑
                </div>
                {scored.map((d) => {
                  const x = (((d.avg_engagement_score ?? 3) - 1) / 4) * 100;
                  const y = 100 - (d.turnover_rate_12m / maxTurnover) * 100;
                  return (
                    <div
                      key={d.department_id || d.department_name || "none"}
                      className="group absolute -translate-x-1/2 -translate-y-1/2"
                      style={{ left: `${x}%`, top: `${y}%` }}
                      title={`${d.department_name || "—"} · eng ${d.avg_engagement_score?.toFixed(2)} · turnover ${fmtPct(d.turnover_rate_12m)}`}
                    >
                      <div
                        className={`h-3 w-3 rounded-full ring-2 ring-bg-1 ${
                          d.flagged ? "bg-danger" : "bg-accent"
                        }`}
                      />
                      <div className="pointer-events-none absolute left-3 top-3 whitespace-nowrap rounded bg-surface px-1.5 py-0.5 text-xs opacity-0 shadow group-hover:opacity-100">
                        {d.department_name || "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-muted text-text-muted">
                  <tr>
                    <th className="px-4 py-2 text-left">Department</th>
                    <th className="px-4 py-2 text-right">Headcount</th>
                    <th className="px-4 py-2 text-right">Engagement</th>
                    <th className="px-4 py-2 text-right">Performance</th>
                    <th className="px-4 py-2 text-right">Turnover</th>
                    <th className="px-4 py-2 text-left">Insight</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.departments.map((d) => (
                    <tr
                      key={d.department_id || d.department_name || "none"}
                      className="border-t border-border"
                    >
                      <td className="px-4 py-2">
                        {d.department_name || "(no department)"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {d.employee_count}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {d.avg_engagement_score != null
                          ? d.avg_engagement_score.toFixed(2)
                          : "—"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {d.avg_performance_rating != null
                          ? d.avg_performance_rating.toFixed(2)
                          : "—"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {fmtPct(d.turnover_rate_12m)}
                      </td>
                      <td className="px-4 py-2">
                        {d.flagged ? (
                          <Badge variant="warning">
                            {d.insight || "Flagged"}
                          </Badge>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Page shell
// ════════════════════════════════════════════════════════════════════

export function PredictPage() {
  const [tab, setTab] = useState<TabId>("insights");
  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Predictions"
        subtitle="AI-rated flight risk, department turnover forecasts, and weekly insights."
      />
      <Tabs
        tabs={[
          { id: "insights", label: "Weekly insights" },
          { id: "risk", label: "Flight risk" },
          { id: "forecast", label: "Turnover forecast" },
          { id: "engagement", label: "Engagement" },
        ]}
        activeTab={tab}
        onChange={(id) => setTab(id as TabId)}
        aria-label="Predictions sections"
      />
      <TabPanel id="insights" activeTab={tab}>
        <InsightsTab />
      </TabPanel>
      <TabPanel id="risk" activeTab={tab}>
        <FlightRiskTab />
      </TabPanel>
      <TabPanel id="forecast" activeTab={tab}>
        <ForecastTab />
      </TabPanel>
      <TabPanel id="engagement" activeTab={tab}>
        <EngagementTab />
      </TabPanel>
      <p className="px-1 text-xs text-text-muted">
        <Brain className="mr-1 inline h-3.5 w-3.5" />
        Powered by Claude Haiku. Pages load cached results — click "Run new
        analysis" on any tab to spend tokens on a fresh snapshot. Predictions
        are advisory only — never use them as the sole basis for an HR
        decision.
      </p>
    </motion.div>
  );
}

export default PredictPage;
